/**
 * Prístup k hľadaniu ArcGISu bez jeho widgetu.
 *
 * Panel nad mapou (`MapSearch`) aj riadok „Adresa" v detaile podnetu
 * potrebujú to isté: geokóder, ktorý appka používala doteraz cez
 * `<arcgis-search>`. Berieme jeho **view model** a nie vlastnú inštanciu
 * `Locator`-a zámerne - view model si predvolené zdroje vyrieši sám, takže sa
 * nemení ani adresa služby, ani spôsob prístupu k nej.
 *
 * Inštancia je jedna na celú appku: naraz je na obrazovke jedna mapa a dve
 * inštancie by znamenali dva `suggest` dotazy na to isté písmeno.
 */
let viewModel: __esri.SearchViewModel | undefined;
let pending: Promise<__esri.SearchViewModel> | undefined;

/**
 * Ako dlho sa čaká na predvolené zdroje, kým sa view model vydá aj tak.
 *
 * Bez stropu by ich nedostupná služba geokódera zaseknúť **navždy** - a s ňou
 * aj hľadanie, ktoré si okrem miest poradí aj bez nich (podnety a plus kód
 * ArcGIS nepotrebujú).
 */
const SOURCES_TIMEOUT_MS = 5000;

export const arcgisSearch = async (
  view?: __esri.MapView | null,
): Promise<__esri.SearchViewModel> => {
  pending ??= Promise.all([
    import('@arcgis/core/widgets/Search/SearchViewModel'),
    import('@arcgis/core/core/reactiveUtils'),
  ]).then(async ([{ default: SearchViewModel }, { whenOnce }]) => {
    const model = new SearchViewModel({
      includeDefaultSources: true,
      // View model má tieto tri veci v defaulte zapnuté a s `view` v ruke ich
      // aj urobí: nakreslí si do mapy vlastnú značku, otvorí nad ňou popup
      // a odletí na ňu. Všetky tri sú tu na škodu:
      //
      // - popup je ten istý problém ako pri vrstvách feedu (`FeedsMap`) -
      //   obsah žije v shadow DOM mapy, kam emotion svoje štýly nevkladá,
      //   takže by v ňom nebola ani téma, ani komponenty z `ui-react`;
      // - značku výsledku kreslí appka sama, aby na nej mala vlastný popis
      //   a dala sa zrušiť klikom mimo;
      // - let robí `FeedsMap` v `onSelectPlace`, takže by mapa letela dvakrát.
      popupEnabled: false,
      resultGraphicEnabled: false,
      autoNavigate: false,
    });

    /**
     * `defaultSources` sa dopĺňajú **asynchrónne**, takže hneď po vytvorení
     * je kolekcia prázdna. Čaká sa tu, nie u volajúceho: kto si model vypýta,
     * chce z neho zdroje - obmedzenie na Slovensko sa vešia na ne a adresa
     * bodu sa berie z URL toho geokóderového.
     *
     * Kým sa nečakalo, prejavilo sa to dvomi vecami, ktoré spolu nevyzerali
     * súvisieť: **prvá** otázka v hľadaní išla nezúžená (na „chodnik" poľské
     * obce) a **riadok s adresou v detaile podnetu zostal na mobile prázdny**
     * - efekt zbehol raz, žiadnu URL nenašiel a druhý raz sa už nespustil.
     * Na desktope adresa bola, lebo tam sa model stihol dokončiť skôr.
     */
    await Promise.race([
      whenOnce(() => model.defaultSources.length > 0),
      new Promise((resolve) => {
        window.setTimeout(resolve, SOURCES_TIMEOUT_MS);
      }),
    ]);

    viewModel = model;
    return model;
  });

  const model = viewModel ?? (await pending);

  // Pohľad dorazí až po pripravení mapy. Podľa neho ArcGIS uprednostní
  // výsledky v okolí výrezu.
  if (view && model.view !== view) {
    model.view = view;
  }

  // Samotný pohľad nestačí: geokóder podľa neho výsledky len zoraďuje, takže
  // „chodnik" vracal poľské obce pred prešovskými ulicami. `countryCode` ich
  // orezáva na Slovensko - appka je portál jedného mesta, takže hľadať mimo
  // republiky nemá kam viesť.
  model.defaultSources.forEach((source) => {
    const locator = source as __esri.LocatorSearchSource;
    if (!locator.url) return;
    locator.countryCode = 'SK';
    locator.maxSuggestions = 5;
  });

  return model;
};

/**
 * Adresa pre bod na mape.
 *
 * URL služby sa nepíše natvrdo - berie sa z predvoleného zdroja view modelu,
 * teda z toho istého miesta, kam chodí hľadanie. Keby sa raz mesto prepolo na
 * vlastný geokóder, stačí zmeniť zdroj a toto pôjde s ním.
 */
export const reverseGeocode = async (
  coordinates: [number, number],
  view?: __esri.MapView | null,
): Promise<string | undefined> => {
  const model = await arcgisSearch(view);
  // `defaultSources` je zmes zdrojov nad vrstvami a nad geokóderom; `url` má
  // len ten druhý, a práve ten vie preložiť bod na adresu.
  const url = model.defaultSources
    .toArray()
    .map((item) => (item as __esri.LocatorSearchSource).url)
    .find(Boolean);

  if (!url) {
    return undefined;
  }

  const [{ locationToAddress }, { default: Point }] = await Promise.all([
    import('@arcgis/core/rest/locator'),
    import('@arcgis/core/geometry/Point'),
  ]);

  const address = await locationToAddress(url, {
    location: new Point({
      longitude: coordinates[0],
      latitude: coordinates[1],
    }),
  });

  // `ShortLabel` je ulica s číslom bez mesta a štátu - v paneli širokom 390 px
  // je celá poštová adresa na dva riadky a mesto v nej je zbytočné, lebo mapa
  // stojí v Prešove.
  return address?.attributes?.ShortLabel || address?.address || undefined;
};
